<script setup lang="ts">
import { computed, inject, watch } from "vue";
import { ThemeButton, ThemeCol, ThemeContainer, ThemeRow } from "@/ui-components";
import { type Breakpoints, useBreakpoints } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import { useUiStore } from "@/stores/ui.ts";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/auth.ts";
import { useConfigStore } from "@/stores/config.ts";
import MerklisteIcon from "@/assets/icons/merkliste.svg?component";
import LoginIcon from "@/assets/icons/login.svg?component";
import LogoutIcon from "@/assets/icons/logout.svg?component";
import SearchIcon from "@/assets/icons/search.svg?component";
import lookAndFeel from "@/config/lookAndFeel";
import { useUserStore } from "@/stores/user.ts";
import { useFeatureTogglesStore } from "@/stores/featureToggles.ts";
import { useCmsHeaderConfig } from "@/composables/useCmsHeaderConfig";

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const authStore = useAuthStore();
const configStore = useConfigStore();
const userStore = useUserStore();
const featureTogglesStore = useFeatureTogglesStore();
const { allowSearch, filtersVisibility } = storeToRefs(uiStore);
const { showOverlay } = uiStore;
const { isAuthenticated, redirect } = storeToRefs(authStore);
const { logout, startSsoLoginFlow } = authStore;
const { isLtiEnabled } = storeToRefs(configStore);
const { userProfile } = storeToRefs(userStore);
const { featureToggles } = storeToRefs(featureTogglesStore);

const { mainItems, supplementalItems, logoUrl, headerText } = useCmsHeaderConfig();
const breakpoints = useBreakpoints(inject("breakpoints") as Breakpoints<string>);

const loginDestination = computed(() =>
  route.path.startsWith("/entry")
    ? { name: "signin" }
    : { name: "signin", query: { redirect_url: route.fullPath } },
);

const isSearchButtonVisible = computed(() => {
  return route.name === "search" || route.name === "lti-search"
    ? false
    : breakpoints.smallerOrEqual("sm").value && !filtersVisibility.value && allowSearch.value;
});

const showMobileFilters = computed(() => {
  if (route.name === "main") return false;
  if (route.name === "search") return breakpoints.smallerOrEqual("md").value;
  if (route.name === "lti-search") return breakpoints.smallerOrEqual("md").value;
  return filtersVisibility.value;
});

const isBookmarkRoute = computed(() => (route.name as string).includes("bookmarks"));

watch(
  () => route.name,
  () => showOverlay(false),
);

const onLoginClick = () => {
  if (featureToggles.value?.skipLoginEntryPage.enabled()) {
    if (!route.path.startsWith("/entry")) {
      redirect.value = route.fullPath;
    }
    startSsoLoginFlow();
  } else {
    router.push(loginDestination.value);
  }
};

const onLogoutClick = async () => {
  await logout();
  await router.push({ name: "main" });
};
</script>

<template>
  <header class="header">
    <ThemeContainer class="header-container" :fluid="true">
      <ThemeRow
        :class="{
          'py-0': lookAndFeel.fullHeightLogo,
          'py-2': !lookAndFeel.fullHeightLogo,
        }"
        align-v="center"
        class="d-flex justify-content-center flex-nowrap"
      >
        <ThemeCol>
          <component
            :is="isLtiEnabled ? 'div' : 'router-link'"
            class="d-block d-flex align-items-center flex-nowrap flex-shrink-0 mr-auto"
            :class="{ 'flex-column': breakpoints.smallerOrEqual('xl').value }"
            to="/"
          >
            <img
              :class="lookAndFeel.fullHeightLogo ? 'logo-full-height' : 'logo-fixed-width'"
              :src="logoUrl"
              alt="Logo"
            />
            <div
              v-if="headerText !== ''"
              class="h4 mb-0 ps-3 text-nowrap"
              :class="{ 'small-header': breakpoints.smallerOrEqual('lg').value }"
            >
              {{ headerText }}
            </div>
          </component>
        </ThemeCol>
        <ThemeCol
          v-if="!breakpoints.smallerOrEqual('md').value"
          class="d-flex justify-content-center flex-shrink-1 m-0-auto"
        >
          <HeaderSearch v-if="allowSearch" />
        </ThemeCol>
        <ThemeCol class="d-flex justify-content-end flex-shrink-2 ml-auto">
          <ThemeButton
            v-if="isSearchButtonVisible"
            class="me-1"
            variant="light"
            :fab="true"
            :icon-center="SearchIcon"
            @click.stop="filtersVisibility = true"
          />
          <template v-if="route.name">
            <ThemeButton
              v-if="isAuthenticated && !isLtiEnabled"
              class="me-1"
              :variant="isBookmarkRoute ? 'primary' : 'light'"
              :fab="true"
              :icon-center="MerklisteIcon"
              aria-label="Merklisten"
              @click="$router.push({ name: 'user-bookmarks' })"
            />
            <HeaderUserProfileButton v-if="isAuthenticated && userProfile.id && !isLtiEnabled" />
            <ThemeButton
              v-if="isAuthenticated && !isLtiEnabled"
              class="me-1"
              variant="light"
              :icon-left="LogoutIcon"
              data-testid="logout-button"
              @click="onLogoutClick"
            >
              Abmelden
            </ThemeButton>
            <ThemeButton
              v-if="!isAuthenticated && !isLtiEnabled"
              class="me-1"
              variant="light"
              :icon-left="LoginIcon"
              data-testid="login-button"
              @click="onLoginClick"
            >
              Anmelden
            </ThemeButton>
          </template>
          <HeaderMenu :main-items="mainItems" :supplemental-items="supplementalItems" />
        </ThemeCol>
      </ThemeRow>
      <ThemeRow v-show="showMobileFilters">
        <ThemeCol class="pb-2">
          <HeaderSearch />
        </ThemeCol>
      </ThemeRow>
    </ThemeContainer>
  </header>
</template>

<style scoped lang="scss">
.header {
  position: sticky;
  top: 0;
  left: 0;
  z-index: var(--header-z-index);
  width: 100%;
  background-color: var(--white);
  :deep(.container-fluid) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.header-container {
  background-color: var(--white);
  display: flex;
  flex-direction: column;
  min-height: var(--header-height);
}

.h4 {
  font-weight: 500;
  color: var(--black);

  &.small-header {
    font-size: 0.75rem;
  }
}

.logo-full-height {
  height: var(--header-height);
  margin-top: 0;
  margin-bottom: 0;
}

.logo-fixed-width {
  width: var(--header-logo-width);
}
</style>
